import type { LotStoryProjectionTimelineEvent } from '../../types/lotStory'

export type StoryLineageGraphRelation = {
  parentLotId: string
  parentLabel: string
  childLotIds: string[]
  childLabels: string[]
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const normalized = String(value ?? '').trim()
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    out.push(normalized)
  })

  return out
}

const isRelationshipEvent = (event: LotStoryProjectionTimelineEvent): boolean => (
  event.eventType === 'split' || event.eventType === 'merge'
)

export const buildStoryLineageGraphRelations = (args: {
  events: LotStoryProjectionTimelineEvent[]
  resolveLotLabel: (lotId: string) => string
}): StoryLineageGraphRelation[] => {
  const childSetByParent = new Map<string, Set<string>>()

  args.events.forEach((event) => {
    if (!isRelationshipEvent(event)) return

    const sourceLotIds = uniqueStrings(event.sourceLotIds)
    const targetLotIds = uniqueStrings(event.targetLotIds)
    if (sourceLotIds.length === 0 || targetLotIds.length === 0) return

    sourceLotIds.forEach((sourceLotId) => {
      if (!childSetByParent.has(sourceLotId)) {
        childSetByParent.set(sourceLotId, new Set<string>())
      }

      const childSet = childSetByParent.get(sourceLotId)
      targetLotIds.forEach((targetLotId) => {
        if (!targetLotId || targetLotId === sourceLotId) return
        childSet?.add(targetLotId)
      })
    })
  })

  return Array.from(childSetByParent.entries())
    .map(([parentLotId, childSet]) => {
      const childLotIds = Array.from(childSet)
      const childLabels = childLotIds
        .map((lotId) => args.resolveLotLabel(lotId))
        .sort((left, right) => left.localeCompare(right))

      return {
        parentLotId,
        parentLabel: args.resolveLotLabel(parentLotId),
        childLotIds,
        childLabels
      }
    })
    .filter((entry) => entry.childLotIds.length > 0)
    .sort((left, right) => left.parentLabel.localeCompare(right.parentLabel))
}